import { prefersReducedMotion } from '@theme/utilities';

/**
 * LBC scroll reveal.
 *
 * Tags elements inside the custom lbc-* sections with [data-lbc-reveal] and
 * flips them to [data-lbc-reveal="in"] as they scroll into view. The hidden
 * state lives in lbc-motion.css and is keyed off that attribute, so if this
 * module never runs the page renders exactly as it did before - content can't
 * be stranded invisible.
 */

/** Distance from the viewport edge at which an element starts revealing. */
const ROOT_MARGIN = '0px 0px -12% 0px';

/** Per-item stagger inside a group, in milliseconds. */
const STAGGER_MS = 70;

/** Cap on stagger so a long grid's last item doesn't lag noticeably. */
const MAX_STAGGER_STEPS = 8;

/**
 * Sections whose content sits above the fold on load. Animating a hero in is a
 * flash, not a flourish, so these are skipped wholesale.
 */
const SKIP_SECTIONS = [
  'lbc-hero-video',
  'lbc-hero-secondary',
  'lbc-brand-hero',
  'lbc-brand-hero-media',
  'wholesale-hero',
];

/** Standalone blocks that reveal as one unit. */
const UNIT_SELECTOR = [
  '[class*="__head"]',
  '[class*="__intro"]',
  '[class*="__text"]',
  '[class*="__copy"]',
  '[class*="__content"]',
  '[class*="__sub"]',
].join(',');

/** Containers whose element children reveal one after another. */
const GROUP_SELECTOR = ['[class*="__grid"]', '[class*="__cards"]', '[class*="__list"]', '[class*="__track"]'].join(',');

/** @type {IntersectionObserver | undefined} */
let observer;

/**
 * @param {Element} el
 * @returns {boolean} True if the element is inside a section we don't animate.
 */
function isSkipped(el) {
  const section = el.closest('section');
  if (!section) return false;
  return SKIP_SECTIONS.some((name) => section.classList.contains(name));
}

/**
 * @param {Element} el
 * @returns {boolean} True if any part of the element is currently on screen.
 */
function isOnScreen(el) {
  const { top, bottom } = el.getBoundingClientRect();
  return bottom > 0 && top < window.innerHeight;
}

/**
 * Prepare one element for reveal.
 * @param {Element} el
 * @param {number} index Position within its group, for stagger.
 * @returns {HTMLElement | null} The element if it was tagged, else null.
 */
function tag(el, index) {
  if (!(el instanceof HTMLElement)) return null;
  if (el.hasAttribute('data-lbc-reveal')) return null;
  if (el.closest('[aria-hidden="true"]')) return null;
  if (isSkipped(el)) return null;
  // Already visible on load: leave it completely untouched so there's no
  // hidden-then-shown flicker in the part of the page the visitor is looking at.
  if (isOnScreen(el)) return null;

  if (index > 0) {
    el.style.setProperty('--lbc-reveal-delay', `${Math.min(index, MAX_STAGGER_STEPS) * STAGGER_MS}ms`);
  }
  el.setAttribute('data-lbc-reveal', '');
  // Observed immediately rather than after arming: if rAF never runs (page
  // loaded in a background tab, say), these still reveal - they just snap
  // instead of animating. Content is never left stranded behind a frame
  // callback that may not fire.
  observer?.observe(el);
  return el;
}

/**
 * Find the reveal candidates inside a root, tag them, then arm their
 * transitions once the hidden state has actually been painted.
 * @param {ParentNode} root
 */
function collect(root) {
  const sections = root.querySelectorAll('section[class*="lbc-"], section[class*="wholesale-"]');
  /** @type {HTMLElement[]} */
  const tagged = [];

  for (const section of sections) {
    const groups = section.querySelectorAll(GROUP_SELECTOR);
    for (const group of groups) {
      [...group.children].forEach((child, i) => {
        const el = tag(child, i);
        if (el) tagged.push(el);
      });
    }

    for (const unit of section.querySelectorAll(UNIT_SELECTOR)) {
      // Anything already inside a tagged element rides along with its parent's
      // animation - tagging it too would double up the fade.
      if (unit.parentElement?.closest('[data-lbc-reveal]')) continue;
      if (unit.closest(GROUP_SELECTOR)) continue;
      const el = tag(unit, 0);
      if (el) tagged.push(el);
    }
  }

  if (!tagged.length) return;

  // Two frames: the first lets the browser paint the un-transitioned hidden
  // state, the second arms the transition. Arming in the same frame as tagging
  // would let the element animate from visible to hidden.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      for (const el of tagged) el.setAttribute('data-lbc-armed', '');
    });
  });
}

function init() {
  if (prefersReducedMotion()) return;

  observer ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;
        el.setAttribute('data-lbc-reveal', 'in');
        observer?.unobserve(el);
        el.addEventListener(
          'transitionend',
          () => {
            el.setAttribute('data-lbc-settled', '');
            // The stagger delay has done its job. Left in place it would also
            // delay this element's hover transition for the rest of the page.
            if (el instanceof HTMLElement) el.style.removeProperty('--lbc-reveal-delay');
          },
          { once: true }
        );
      }
    },
    { rootMargin: ROOT_MARGIN, threshold: 0 }
  );

  collect(document);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Theme editor: re-scan a section after it's re-rendered.
document.addEventListener('shopify:section:load', (event) => {
  if (prefersReducedMotion()) return;
  const target = /** @type {CustomEvent} */ (event).target;
  if (target instanceof HTMLElement) collect(target);
});
